/**
 * InfiniteScrollShiftDataProvider - Data provider for infinite scroll calendar mode
 *
 * This provider manages shift data for the infinite scroll calendar, using chunked
 * loading to efficiently handle the full date range from business creation to +2 years.
 *
 * Key features:
 * - Chunked data loading in monthly segments
 * - Automatic chunk loading based on visible range
 * - Loading state tracking per day for skeleton display
 * - Compatible with existing ShiftDataProvider interface
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, ReactNode } from 'react';
import { startOfMonth, endOfMonth, startOfDay, isBefore, addMonths, getMonth, getYear } from 'date-fns';
import { Shift, DayShift, BusinessClosedDate, DateClosureStatus } from '@/types';
import { Tables } from '@/integrations/supabase/types';
import { TimeLogQueryResult } from '@/types/shift-components';
import { ShiftTemplate } from '../ShiftTemplateItem';
import { useEmployees } from '@/hooks/employees';
import { useBusinesses } from '@/hooks/business/useBusinesses';
import { useSimpleDisplayHours, type SimpleDisplayHoursRange } from '@/hooks/scheduling/useSimpleDisplayHours';
import { usePeriodStatuses } from '@/hooks/analytics/usePeriodStatuses';
import { useDayTemplates } from '@/hooks/scheduling/useDayTemplates';
import { useBusinessTime } from '@/hooks/ui/useBusinessTime';
import { useShiftStatusService } from '@/hooks/scheduling/useShiftStatusService';
import { employeeAvailabilityCache } from '@/lib/employees/EmployeeAvailabilityCache';
import { useInvalidateShifts } from '@/hooks/scheduling/useShiftsQuery';
import { useAssignEmployee, useBatchAssignEmployee } from '@/hooks/scheduling/useShiftMutations';
import { useHistoricalEdit } from '@/context/HistoricalEditContext';
import { useBusinessClosedDates } from '@/hooks/business/useBusinessClosedDates';
import { useInfiniteScrollDateRange, type InfiniteScrollDateRange } from '@/hooks/scheduling/useInfiniteScrollDateRange';
import { useChunkedShiftData, type ChunkLoadingState } from '@/hooks/scheduling/useChunkedShiftData';
import { getMonthKey } from '@/hooks/scheduling/useInfiniteScrollDateRange';

// Types
type Event = Tables<'events'>;
type EventAssignment = Tables<'event_assignments'>;

interface EventWithAssignments extends Event {
  event_assignments: (EventAssignment & {
    employees: {
      name: string;
      email: string;
    };
  })[];
}

// Extended context for infinite scroll mode
interface InfiniteScrollShiftDataContextValue {
  // Core data (compatible with ShiftDataProvider)
  shifts: DayShift[];
  employees: any[];
  timeLogs: TimeLogQueryResult[];
  shiftTemplates: ShiftTemplate[];
  dayTemplates: any[];
  events: EventWithAssignments[];
  periodStatuses: any;
  selectedBusinessDetails: any;
  periodEmployeeStatuses: any[];

  // Display hours (compatible with ShiftDataProvider)
  currentMonthDisplayHours: any;
  displayStartTime: Date | null;
  displayEndTime: Date | null;
  simpleDisplayHours: {
    range: SimpleDisplayHoursRange;
    calculationMethod: 'user-override' | 'opening-hours' | 'fallback';
    canExpand: boolean;
    expandRange: (newRange: Partial<SimpleDisplayHoursRange>) => void;
    resetToAuto: () => void;
    isLoading: boolean;
  };

  // Loading states
  loadingShifts: boolean;
  loadingEmployees: boolean;
  loadingEvents: boolean;
  loadingShiftTemplates: boolean;
  loadingDayTemplates: boolean;
  loadingDisplayHours: boolean;
  periodStatusesLoading: boolean;

  // Error states
  periodStatusesError: string | null;

  // Business data
  businessId: string | null;
  businessTimezone: string;
  countryCode: string | null;
  allShiftsAssigned: boolean;

  // Performance stats
  performanceStats: any;

  // Data actions (compatible with ShiftDataProvider)
  refetchShifts: () => void;
  refetchPeriodStatuses: (() => void) | null;
  handleShiftCreated: () => void;
  handleShiftUpdated: () => void;
  handleShiftDeleted: () => void;
  handleAssignEmployeeToShift: (shiftId: string, employeeId: string | null) => Promise<void>;
  handleBatchAssignEmployee: (shiftIds: string[], employeeId: string | null) => Promise<void>;
  fetchShiftTemplates: () => Promise<void>;

  // Template actions
  handleEditDayTemplate: (id: string) => void;
  handleDeleteDayTemplate: (id: string) => void;

  // Day template edit dialog state
  isEditDialogOpen: boolean;
  editedTemplate: any | null;
  editedName: string;
  editedDescription: string;
  editedShifts: any[];
  setEditedName: (name: string) => void;
  setEditedDescription: (description: string) => void;
  handleSaveTemplate: () => Promise<void>;
  closeEditDialog: () => void;

  // Closed dates
  closedDatesMap: Map<string, BusinessClosedDate>;
  closedDatesLoading: boolean;
  addClosedDate: (date: Date, reason?: string) => Promise<BusinessClosedDate>;
  removeClosedDate: (id: string) => Promise<void>;
  isDateClosed: (date: Date) => DateClosureStatus;
  checkExistingShifts: (date: Date) => Promise<{ count: number; hasAssignedShifts: boolean }>;
  refreshClosedDates: () => Promise<void>;

  // === INFINITE SCROLL SPECIFIC ===

  /** Full date range for infinite scroll */
  dateRange: InfiniteScrollDateRange;

  /** All days in the infinite scroll range */
  allDays: Date[];

  /** Chunk loading states */
  chunkStates: Map<string, ChunkLoadingState>;

  /** Check if a specific day is loading */
  isDayLoading: (dayIndex: number) => boolean;

  /** Update visible range (triggers chunk loading) */
  setVisibleRange: (startIndex: number, endIndex: number) => void;

  /** Get shifts for a specific day (from chunked data) */
  getShiftsForDay: (date: Date) => DayShift[];

  /** Whether any chunk is currently loading */
  isAnyChunkLoading: boolean;

  /** Prefetch a specific month */
  prefetchMonth: (year: number, month: number) => void;
}

const InfiniteScrollShiftDataContext = createContext<InfiniteScrollShiftDataContextValue | undefined>(undefined);

interface InfiniteScrollShiftDataProviderProps {
  children: ReactNode;
  businessId: string | null;
  /** Reference date for display hours calculation (typically today or scroll position) */
  referenceDate: Date;
}

export const InfiniteScrollShiftDataProvider: React.FC<InfiniteScrollShiftDataProviderProps> = ({
  children,
  businessId,
  referenceDate
}) => {
  // Business data
  const { businessTimezone } = useBusinessTime();
  const { businesses } = useBusinesses();

  // Timezone-aware shift status service
  const shiftStatusService = useShiftStatusService();

  // Historical edit context
  const { recordHistoricalEdit, isHistoricalEditEnabled } = useHistoricalEdit();

  // Derived business data
  const selectedBusinessDetails = businesses?.find(b => b.id === businessId) || null;
  const countryCode = selectedBusinessDetails?.country_code || null;
  const openingHours = selectedBusinessDetails?.opening_hours || [];

  // === INFINITE SCROLL DATE RANGE ===
  const dateRange = useInfiniteScrollDateRange({
    businessCreatedAt: selectedBusinessDetails?.created_at,
    futureYears: 2,
    enabled: true
  });

  // === CHUNKED SHIFT DATA ===
  const {
    employees,
    loading: loadingEmployees
  } = useEmployees({
    businessId: businessId,
    dataLevel: 'standard'
  });

  const chunkedData = useChunkedShiftData({
    businessId,
    employees: employees || [],
    rangeStartDate: dateRange.startDate,
    rangeEndDate: dateRange.endDate,
    enabled: !!businessId && dateRange.allDays.length > 0,
    bufferMonths: 1,
    debounceMs: 150
  });

  // Calculate closed dates range
  const closedDatesRange = useMemo(() => {
    const today = new Date();
    return {
      start: startOfMonth(today),
      end: endOfMonth(addMonths(today, 1))
    };
  }, []);

  // Closed dates for marking individual days as closed
  const {
    closedDatesMap,
    loading: closedDatesLoading,
    addDate: addClosedDate,
    removeDate: removeClosedDate,
    isDateClosed,
    checkExistingShifts,
    refresh: refreshClosedDates
  } = useBusinessClosedDates({
    businessId: businessId || null,
    startDate: closedDatesRange.start,
    endDate: closedDatesRange.end,
    openingHours
  });

  // Period statuses for analytics
  const {
    periodStatuses,
    loading: periodStatusesLoading,
    error: periodStatusesError,
    refetch: refetchPeriodStatuses
  } = usePeriodStatuses(businessId);

  // Mock data
  const [timeLogs] = useState<TimeLogQueryResult[]>([]);
  const [periodEmployeeStatuses] = useState<any[]>([]);

  const {
    dayTemplates,
    loadingTemplates: loadingDayTemplates,
    handleEditTemplate: handleEditDayTemplate,
    handleDeleteTemplate: handleDeleteDayTemplate,
    isEditDialogOpen,
    editedTemplate,
    editedName,
    editedDescription,
    editedShifts,
    setEditedName,
    setEditedDescription,
    handleSaveTemplate,
    closeEditDialog
  } = useDayTemplates({ businessId });

  // Prepare shifts for display hours calculation
  const shiftsForDisplayHours = useMemo(() => {
    if (!chunkedData.shifts || chunkedData.shifts.length === 0) return [];
    return chunkedData.shifts.map(shift => ({
      startTime: `${shift.startTime.getHours().toString().padStart(2, '0')}:${shift.startTime.getMinutes().toString().padStart(2, '0')}`,
      endTime: `${shift.endTime.getHours().toString().padStart(2, '0')}:${shift.endTime.getMinutes().toString().padStart(2, '0')}`
    }));
  }, [chunkedData.shifts]);

  // Simple display hours
  const simpleDisplayHours = useSimpleDisplayHours({
    businessId: businessId || '',
    date: referenceDate.toISOString().split('T')[0],
    openingHours: selectedBusinessDetails?.opening_hours || [],
    shifts: shiftsForDisplayHours,
    mode: 'multi-day'
  });

  const loadingDisplayHours = simpleDisplayHours.isLoading;

  // State for templates and events
  const [shiftTemplates, setShiftTemplates] = useState<ShiftTemplate[]>([]);
  const [events, setEvents] = useState<EventWithAssignments[]>([]);
  const [loadingShiftTemplates, setLoadingShiftTemplates] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);

  // Display times from simpleDisplayHours
  const currentMonthDisplayHours = null;

  const displayStartTime = useMemo(() => {
    const { startTime } = simpleDisplayHours.range;
    return new Date(`1970-01-01T${startTime}:00`);
  }, [simpleDisplayHours.range.startTime]);

  const displayEndTime = useMemo(() => {
    const { endTime, end } = simpleDisplayHours.range;
    if (end >= 24) {
      return new Date(`1970-01-01T23:59:00`);
    }
    return new Date(`1970-01-01T${endTime}:00`);
  }, [simpleDisplayHours.range.endTime, simpleDisplayHours.range.end]);

  // Calculate if all shifts are assigned
  const allShiftsAssigned = chunkedData.shifts?.every(shift => shift.employeeId) || false;

  // Performance stats
  const performanceStats = {
    totalShifts: chunkedData.shifts?.length || 0,
    assignedShifts: chunkedData.shifts?.filter(s => s.employeeId).length || 0,
    totalEmployees: employees?.length || 0,
    loadedChunks: Array.from(chunkedData.chunkStates.values()).filter(s => s.isLoaded).length,
    loadingChunks: Array.from(chunkedData.chunkStates.values()).filter(s => s.isLoading).length,
  };

  // Get invalidation helper
  const { invalidateAll } = useInvalidateShifts();

  // Use React Query mutations
  const assignEmployeeMutation = useAssignEmployee();
  const batchAssignMutation = useBatchAssignEmployee();

  // Data manipulation actions
  const refetchShifts = useCallback(() => {
    if (businessId) {
      invalidateAll(businessId);
    }
    refetchPeriodStatuses?.();
  }, [businessId, invalidateAll, refetchPeriodStatuses]);

  const handleShiftCreated = useCallback(() => {
    refetchShifts();
    employeeAvailabilityCache.invalidateCache('shift-created');
  }, [refetchShifts]);

  const handleShiftUpdated = useCallback(() => {
    refetchShifts();
    employeeAvailabilityCache.invalidateCache('shift-updated');
  }, [refetchShifts]);

  const handleShiftDeleted = useCallback(() => {
    refetchShifts();
    employeeAvailabilityCache.invalidateCache('shift-deleted');
  }, [refetchShifts]);

  const handleAssignEmployeeToShift = useCallback(async (shiftId: string, employeeId: string | null) => {
    try {
      const targetShift = chunkedData.shifts.find(s => s.id === shiftId);
      const oldEmployeeId = targetShift?.employeeId;

      await assignEmployeeMutation.mutateAsync({ shiftId, employeeId });

      if (targetShift && businessId) {
        const today = startOfDay(new Date());
        const shiftDay = startOfDay(targetShift.startTime);
        const isHistoricalShift = isBefore(shiftDay, today);

        if (isHistoricalShift && isHistoricalEditEnabled) {
          await recordHistoricalEdit(
            shiftId,
            businessId,
            'employee_changed',
            { employeeId: oldEmployeeId },
            { employeeId: employeeId }
          );
        }
      }
    } catch (error) {
      console.error('Error assigning employee to shift:', error);
      throw error;
    }
  }, [assignEmployeeMutation, chunkedData.shifts, businessId, recordHistoricalEdit, isHistoricalEditEnabled]);

  const handleBatchAssignEmployee = useCallback(async (shiftIds: string[], employeeId: string | null) => {
    try {
      await batchAssignMutation.mutateAsync({ shiftIds, employeeId });

      if (businessId && isHistoricalEditEnabled) {
        const today = startOfDay(new Date());

        for (const shiftId of shiftIds) {
          const targetShift = chunkedData.shifts.find(s => s.id === shiftId);
          if (targetShift) {
            const shiftDay = startOfDay(targetShift.startTime);
            const isHistoricalShift = isBefore(shiftDay, today);

            if (isHistoricalShift) {
              await recordHistoricalEdit(
                shiftId,
                businessId,
                'employee_changed',
                { employeeId: targetShift.employeeId },
                { employeeId: employeeId }
              );
            }
          }
        }
      }
    } catch (error) {
      console.error('Error batch assigning employees:', error);
      throw error;
    }
  }, [batchAssignMutation, chunkedData.shifts, businessId, recordHistoricalEdit, isHistoricalEditEnabled]);

  const fetchShiftTemplates = useCallback(async () => {
    if (!businessId) return;

    setLoadingShiftTemplates(true);
    try {
      setShiftTemplates([]);
    } catch (error) {
      console.error('Error fetching shift templates:', error);
    } finally {
      setLoadingShiftTemplates(false);
    }
  }, [businessId]);

  // Load shift templates when businessId changes
  useEffect(() => {
    if (businessId) {
      fetchShiftTemplates();
    }
  }, [businessId, fetchShiftTemplates]);

  // === INFINITE SCROLL SPECIFIC FUNCTIONS ===

  // Set visible range - wrapper for chunked data hook
  const setVisibleRange = useCallback((startIndex: number, endIndex: number) => {
    chunkedData.setVisibleRange(startIndex, endIndex, dateRange.allDays);
  }, [chunkedData, dateRange.allDays]);

  // Check if a specific day is loading
  const isDayLoading = useCallback((dayIndex: number): boolean => {
    return chunkedData.isDayLoading(dayIndex, dateRange.allDays);
  }, [chunkedData, dateRange.allDays]);

  // Get shifts for a specific day
  const getShiftsForDay = useCallback((date: Date): DayShift[] => {
    const dayKey = startOfDay(date).toISOString();
    return chunkedData.shiftsByDay.get(dayKey) || [];
  }, [chunkedData.shiftsByDay]);

  const value: InfiniteScrollShiftDataContextValue = {
    // Core data
    shifts: chunkedData.shifts || [],
    employees: employees || [],
    timeLogs: timeLogs || [],
    shiftTemplates,
    dayTemplates: dayTemplates || [],
    events,
    periodStatuses,
    selectedBusinessDetails,
    periodEmployeeStatuses: periodEmployeeStatuses || [],

    // Display hours
    currentMonthDisplayHours,
    displayStartTime,
    displayEndTime,
    simpleDisplayHours,

    // Loading states
    loadingShifts: chunkedData.isAnyChunkLoading && chunkedData.shifts.length === 0,
    loadingEmployees,
    loadingEvents,
    loadingShiftTemplates,
    loadingDayTemplates,
    loadingDisplayHours,
    periodStatusesLoading,

    // Error states
    periodStatusesError,

    // Business data
    businessId,
    businessTimezone,
    countryCode,
    allShiftsAssigned,

    // Performance stats
    performanceStats,

    // Actions
    refetchShifts,
    refetchPeriodStatuses,
    handleShiftCreated,
    handleShiftUpdated,
    handleShiftDeleted,
    handleAssignEmployeeToShift,
    handleBatchAssignEmployee,
    fetchShiftTemplates,
    handleEditDayTemplate,
    handleDeleteDayTemplate,

    // Day template edit dialog state
    isEditDialogOpen,
    editedTemplate,
    editedName,
    editedDescription,
    editedShifts,
    setEditedName,
    setEditedDescription,
    handleSaveTemplate,
    closeEditDialog,

    // Closed dates
    closedDatesMap,
    closedDatesLoading,
    addClosedDate,
    removeClosedDate,
    isDateClosed,
    checkExistingShifts,
    refreshClosedDates,

    // Infinite scroll specific
    dateRange,
    allDays: dateRange.allDays,
    chunkStates: chunkedData.chunkStates,
    isDayLoading,
    setVisibleRange,
    getShiftsForDay,
    isAnyChunkLoading: chunkedData.isAnyChunkLoading,
    prefetchMonth: chunkedData.prefetchMonth,
  };

  return (
    <InfiniteScrollShiftDataContext.Provider value={value}>
      {children}
    </InfiniteScrollShiftDataContext.Provider>
  );
};

export const useInfiniteScrollShiftData = (): InfiniteScrollShiftDataContextValue => {
  const context = useContext(InfiniteScrollShiftDataContext);
  if (context === undefined) {
    throw new Error('useInfiniteScrollShiftData must be used within an InfiniteScrollShiftDataProvider');
  }
  return context;
};

/**
 * Safe version of useInfiniteScrollShiftData that returns null instead of throwing
 * when used outside of InfiniteScrollShiftDataProvider (e.g., when in monthly mode)
 */
export const useInfiniteScrollShiftDataSafe = (): InfiniteScrollShiftDataContextValue | null => {
  const context = useContext(InfiniteScrollShiftDataContext);
  return context ?? null;
};

/**
 * Hook to check if we're in infinite scroll mode
 * Returns true if InfiniteScrollShiftDataProvider is available
 */
export const useIsInfiniteScrollMode = (): boolean => {
  const context = useContext(InfiniteScrollShiftDataContext);
  return context !== undefined;
};
